import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Check,
  Clock,
  Inbox,
  Loader2,
  MessageSquare,
  Phone,
  RefreshCw,
  Settings,
  X,
} from 'lucide-react';
import {
  holidayRequestApi,
  managerInboxApi,
  messageApi,
  shiftMarketplaceApi,
  sickLeaveRequestApi,
  unavailabilityRequestApi,
  type ManagerInboxItem,
  type ManagerInboxResponse,
  type ManagerInboxTypeSetting,
} from '../services/api';
import { useLocationContext } from '../components/Layout';
import InboxChatModal from '../components/InboxChatModal';
import InboxItemDetailsModal, { inboxDetailsLabel } from '../components/InboxItemDetailsModal';
import type { MessageConversation } from '../types';
import { formatCompanyDateTime } from '../utils/companyDateFormat';

type FilterId = 'all' | 'attendance' | 'leave' | 'compliance' | 'payroll' | 'coverage';
type LaneId = 'need_action' | 'waiting' | 'muted' | 'watching' | 'done';
type OwnershipId = 'all' | 'owned' | 'escalated';
type NoteAction = 'mark_responded' | 'deny';

const FILTERS: { id: FilterId; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'attendance', label: 'Attendance' },
  { id: 'leave', label: 'Leave' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'payroll', label: 'Payroll' },
  { id: 'coverage', label: 'Coverage' },
];

const OWNERSHIP_FILTERS: { id: OwnershipId; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'owned', label: 'Owned by You' },
  { id: 'escalated', label: 'Escalated to you' },
];

const ATTENDANCE_TYPES = new Set(['absent_today', 'missed_clock_out', 'late_today']);
const LEAVE_TYPES = new Set(['holiday', 'sick', 'unavailability']);
const COMPLIANCE_TYPES = new Set(['visa_expired', 'visa_expiring', 'licence_expired', 'licence_expiring']);
const PAYROLL_TYPES = new Set(['timecard']);
const COVERAGE_TYPES = new Set(['marketplace']);

function matchesFilter(item: ManagerInboxItem, filter: FilterId) {
  if (filter === 'all') return true;
  if (filter === 'attendance') return ATTENDANCE_TYPES.has(item.type);
  if (filter === 'leave') return LEAVE_TYPES.has(item.type);
  if (filter === 'compliance') return COMPLIANCE_TYPES.has(item.type);
  if (filter === 'payroll') return PAYROLL_TYPES.has(item.type);
  return COVERAGE_TYPES.has(item.type);
}

function matchesOwnership(item: ManagerInboxItem, ownership: OwnershipId) {
  if (ownership === 'all') return true;
  if (ownership === 'owned') return item.viewerRole === 'owner';
  return item.viewerRole === 'escalated';
}

function needsEditToResolve(type: string) {
  return type.startsWith('licence_') || type.startsWith('visa_');
}

function openPathInNewWindow(path: string) {
  const url = /^https?:\/\//i.test(path)
    ? path
    : `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

function severityLabel(severity: string) {
  if (severity === 'blocking') return 'Blocking';
  if (severity === 'today') return 'Today';
  if (severity === 'payroll') return 'Payroll';
  if (severity === 'this_week') return 'This week';
  return 'Upcoming';
}

function severityClass(severity: string) {
  if (severity === 'blocking') return 'bg-red-50 text-red-700 border-red-200';
  if (severity === 'today') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (severity === 'payroll') return 'bg-violet-50 text-violet-700 border-violet-200';
  if (severity === 'this_week') return 'bg-blue-50 text-blue-700 border-blue-200';
  return 'bg-gray-50 text-gray-600 border-gray-200';
}

function statusClass(status: string, outcome?: string | null) {
  if (status === 'resolved' && (outcome || '').toLowerCase().startsWith('auto')) {
    return 'bg-slate-100 text-slate-800 border-slate-300';
  }
  if (status === 'open') return 'bg-blue-50 text-blue-800 border-blue-200';
  if (status === 'waiting_for_employee') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (status === 'employee_responded') return 'bg-teal-50 text-teal-800 border-teal-200';
  if (status === 'waiting_for_manager') return 'bg-violet-50 text-violet-800 border-violet-200';
  if (status === 'resolved') return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  return 'bg-gray-50 text-gray-600 border-gray-200';
}

function inboxSince(item: ManagerInboxItem): Date | null {
  const raw = item.createdAt || item.occurredAt;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

function inboxAgeLabel(item: ManagerInboxItem): string | null {
  const date = inboxSince(item);
  if (!date) return null;
  const mins = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 day old';
  return `${days} days old`;
}

function inboxAgeTone(item: ManagerInboxItem): 'fresh' | 'normal' | 'aging' | 'overdue' | 'muted' {
  if ((item.lane ?? '') === 'done' || (item.lane ?? '') === 'muted') return 'muted';
  const sla = item.escalationWorkingDays ?? 0;
  const pending = item.workingDaysPending ?? 0;
  if (sla > 0 && pending >= sla) return 'overdue';
  if (sla > 0 && pending >= Math.max(1, sla - 1)) return 'aging';
  const date = inboxSince(item);
  if (!date) return 'normal';
  const days = (Date.now() - date.getTime()) / 86400000;
  if (days < 1) return 'fresh';
  if (days >= 3) return 'aging';
  return 'normal';
}

function inboxAgeClass(tone: ReturnType<typeof inboxAgeTone>) {
  if (tone === 'overdue') return 'bg-red-50 text-red-700 border-red-200 font-semibold';
  if (tone === 'aging') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (tone === 'fresh') return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (tone === 'muted') return 'bg-gray-50 text-gray-500 border-gray-200';
  return 'bg-gray-50 text-gray-600 border-gray-200';
}

function inboxRowAccent(tone: ReturnType<typeof inboxAgeTone>) {
  if (tone === 'overdue') return 'border-l-4 border-l-red-500';
  if (tone === 'aging') return 'border-l-4 border-l-amber-400';
  return 'border-l-4 border-l-transparent';
}

function notePrompt(action: NoteAction) {
  if (action === 'mark_responded') return 'What did they say? (optional)';
  return 'Reason';
}

function endOfLocalDay(date: Date) {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
}

function nextWorkingDayEnd() {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  while (date.getDay() === 0 || date.getDay() === 6) date.setDate(date.getDate() + 1);
  return endOfLocalDay(date);
}

function muteMaxDate() {
  const date = new Date();
  date.setDate(date.getDate() + 14);
  return date;
}

function toDateInputValue(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

type ChatIntent = 'request_info' | 'ask_manager';

function unreadLabel(count?: number) {
  const n = count ?? 0;
  if (n <= 0) return null;
  return n > 99 ? '99+' : String(n);
}

function InboxUnreadPip({ count }: { count?: number }) {
  const label = unreadLabel(count);
  if (!label) return null;
  return (
    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center leading-none">
      {label}
    </span>
  );
}

function InboxChatButton({
  count,
  label,
  disabled,
  onClick,
}: {
  count?: number;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="relative inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
    >
      <MessageSquare className="h-3.5 w-3.5" />
      {label}
      <InboxUnreadPip count={count} />
    </button>
  );
}

const ManagerInbox: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLocationId } = useLocationContext();
  const [data, setData] = useState<ManagerInboxResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterId>('all');
  const [ownership, setOwnership] = useState<OwnershipId>('all');
  const [lane, setLane] = useState<LaneId>('need_action');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [noteAction, setNoteAction] = useState<NoteAction | null>(null);
  const [note, setNote] = useState('');
  const [muteOpen, setMuteOpen] = useState(false);
  const [muteDate, setMuteDate] = useState(toDateInputValue(nextWorkingDayEnd()));
  const [actionError, setActionError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState<ManagerInboxTypeSetting[]>([]);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [chatItem, setChatItem] = useState<ManagerInboxItem | null>(null);
  const [chatMessageId, setChatMessageId] = useState<number | null>(null);
  const [chatConversations, setChatConversations] = useState<MessageConversation[]>([]);
  const [smsComposer, setSmsComposer] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);
  const [detailsItem, setDetailsItem] = useState<ManagerInboxItem | null>(null);
  const [chatIntent, setChatIntent] = useState<ChatIntent | null>(null);
  const askedViaChatRef = React.useRef(false);
  const pendingChatActionRef = React.useRef<ChatIntent | null>(null);
  const chatOpenGenRef = React.useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await managerInboxApi.get(selectedLocationId);
      setData(result);
      setSelectedId((current) => {
        if (current && result.items.some((i) => i.id === current)) return current;
        return result.items[0]?.id ?? null;
      });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setError(err?.response?.data?.message || 'Could not load your inbox.');
    } finally {
      setLoading(false);
    }
  }, [selectedLocationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const items = useMemo(
    () =>
      (data?.items ?? []).filter(
        (item) =>
          (item.lane ?? 'need_action') === lane
          && matchesFilter(item, filter)
          && (lane !== 'need_action' || matchesOwnership(item, ownership)),
      ),
    [data?.items, filter, lane, ownership],
  );

  const ownershipCounts = useMemo(() => {
    const inNeedAction = (data?.items ?? []).filter(
      (item) => (item.lane ?? 'need_action') === 'need_action' && matchesFilter(item, filter),
    );
    return {
      all: inNeedAction.length,
      owned: inNeedAction.filter((item) => item.viewerRole === 'owner').length,
      escalated: inNeedAction.filter((item) => item.viewerRole === 'escalated').length,
    };
  }, [data?.items, filter]);

  const selected = items.find((i) => i.id === selectedId) ?? items[0] ?? null;

  const runDomainDecision = async (item: ManagerInboxItem, action: 'approve' | 'deny', reason?: string) => {
    if (!item.entityId) return;
    if (item.type === 'holiday') {
      if (action === 'approve') await holidayRequestApi.approve(item.entityId);
      else await holidayRequestApi.reject(item.entityId, reason || 'Not approved');
    } else if (item.type === 'unavailability') {
      if (action === 'approve') await unavailabilityRequestApi.approve(item.entityId);
      else await unavailabilityRequestApi.reject(item.entityId, reason || 'Not approved');
    } else if (item.type === 'sick') {
      if (action === 'approve') await sickLeaveRequestApi.approve(item.entityId);
      else await sickLeaveRequestApi.reject(item.entityId, { rejectionReason: reason || 'Not approved' });
    } else if (item.type === 'timecard') {
      throw new Error('Open the timecard to review hours before approving.');
    } else if (item.type === 'marketplace') {
      if (action === 'approve') await shiftMarketplaceApi.approve(item.entityId);
      else await shiftMarketplaceApi.reject(item.entityId, reason);
    } else {
      throw new Error('This action is not available on this item.');
    }
  };

  const runTransition = async (item: ManagerInboxItem, action: string, extraNote?: string, outcome?: string) => {
    setBusy(true);
    setActionError(null);
    try {
      if (action === 'approve' || action === 'deny') {
        await runDomainDecision(item, action, extraNote);
      }
      const needsInboxMove =
        action === 'request_info' ||
        action === 'ask_manager' ||
        action === 'mark_responded' ||
        action === 'take_over' ||
        action === 'mute' ||
        action === 'unmute';
      if (needsInboxMove) {
        await managerInboxApi.transition({
          itemId: item.id,
          action,
          note: extraNote,
          outcome,
        });
      }
      setNoteAction(null);
      setNote('');
      setMuteOpen(false);
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setActionError(err?.response?.data?.message || err?.message || 'Action failed.');
    } finally {
      setBusy(false);
    }
  };

  const markInboxFixed = async (item: ManagerInboxItem) => {
    if (item.type.startsWith('visa_') || item.type.startsWith('licence_')) setDetailsItem(null);
    await load();
  };

  const submitNote = (item: ManagerInboxItem) => {
    if (!noteAction) return;
    if (noteAction !== 'mark_responded' && !note.trim()) return;
    void runTransition(item, noteAction, note.trim() || undefined, noteAction === 'deny' ? 'rejected' : undefined);
  };

  const needAction = data?.needActionCount ?? 0;
  const waiting = data?.waitingCount ?? 0;
  const muted = data?.mutedCount ?? 0;
  const watching = data?.watchingCount ?? 0;
  const resolvedToday = data?.resolvedTodayCount ?? 0;

  const muteUntil = async (item: ManagerInboxItem, until: Date) => {
    setBusy(true);
    setActionError(null);
    try {
      await managerInboxApi.transition({
        itemId: item.id,
        action: 'mute',
        muteUntil: until.toISOString(),
      });
      setMuteOpen(false);
      setLane('muted');
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setActionError(err?.response?.data?.message || err?.message || 'Could not mute this item.');
    } finally {
      setBusy(false);
    }
  };

  const openSettings = async () => {
    setSettingsOpen(true);
    try {
      setSettings(await managerInboxApi.getSettings());
    } catch {
      setActionError('Could not load escalation settings.');
    }
  };

  const saveSettings = async () => {
    setSettingsBusy(true);
    try {
      await managerInboxApi.saveSettings(settings);
      setSettingsOpen(false);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setActionError(err?.response?.data?.message || err?.message || 'Could not save settings.');
    } finally {
      setSettingsBusy(false);
    }
  };

  const closeChat = () => {
    chatOpenGenRef.current += 1;
    setChatItem(null);
    setChatMessageId(null);
    setChatConversations([]);
    setSmsComposer(false);
    askedViaChatRef.current = false;
    pendingChatActionRef.current = null;
    setChatIntent(null);
    void load();
  };

  const openChat = async (
    item: ManagerInboxItem,
    options?: { openSms?: boolean; intent?: ChatIntent },
  ) => {
    setActionError(null);
    pendingChatActionRef.current = options?.intent ?? null;
    setChatIntent(options?.intent ?? null);
    askedViaChatRef.current =
      !options?.intent &&
      (item.status === 'waiting_for_employee' || item.status === 'employee_responded');
    setChatItem({ ...item, unreadCount: 0 });
    setChatMessageId(null);
    setChatConversations([]);
    setSmsComposer(Boolean(options?.openSms));
    const gen = ++chatOpenGenRef.current;
    try {
      const kind =
        options?.intent === 'ask_manager'
          ? 'manager'
          : options?.intent === 'request_info'
            ? 'employee'
            : 'chat';
      const { messageId } = await managerInboxApi.openChat(item.id, kind);
      const conversations = await messageApi.getConversations(messageId);
      await messageApi.markRead(messageId);
      if (chatOpenGenRef.current !== gen) return;
      setChatMessageId(messageId);
      setChatConversations(conversations);
    } catch (e: unknown) {
      if (chatOpenGenRef.current !== gen) return;
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setChatItem(null);
      setChatMessageId(null);
      setChatConversations([]);
      setSmsComposer(false);
      setChatIntent(null);
      pendingChatActionRef.current = null;
      setActionError(err?.response?.data?.message || err?.message || 'Could not open chat.');
    }
  };

  const handleChatMessageSent = (item: ManagerInboxItem, text: string) => {
    if (askedViaChatRef.current) return;
    const intent =
      pendingChatActionRef.current ??
      (item.actions.includes('request_info') && item.viewerRole === 'owner'
        ? 'request_info'
        : null);
    if (!intent || !item.actions.includes(intent)) return;
    askedViaChatRef.current = true;
    pendingChatActionRef.current = null;
    void runTransition(item, intent, text.trim() || 'Asked via chat');
  };

  const handleSendSms = async (item: ManagerInboxItem, message: string) => {
    setSendingSms(true);
    try {
      await managerInboxApi.sendSms(item.id, message);
      askedViaChatRef.current = true;
      if (chatMessageId) {
        setChatConversations(await messageApi.getConversations(chatMessageId));
      }
      await load();
    } finally {
      setSendingSms(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-[#F4F7F9] min-h-0">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Inbox className="h-5 w-5 text-blue-600" />
              <h1 className="text-xl font-semibold text-gray-900">Inbox</h1>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              The line manager owns the item when assigned; otherwise the location manager does. Higher managers can watch and take over. Escalation notifies the next level without moving ownership.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void openSettings()}
              className="text-sm text-gray-600 border border-gray-200 bg-white rounded-md px-3 py-1.5 hover:bg-gray-50 inline-flex items-center gap-1.5"
            >
              <Settings className="h-3.5 w-3.5" />
              SLAs
            </button>
            <button
              type="button"
              onClick={() => navigate('/my-team?tab=history&scope=all')}
              className="text-sm text-gray-600 border border-gray-200 bg-white rounded-md px-3 py-1.5 hover:bg-gray-50"
            >
              Delegations
            </button>
            <button
              type="button"
              onClick={() => void load()}
              className="inline-flex items-center gap-1.5 text-sm text-gray-700 border border-gray-200 bg-white rounded-md px-3 py-1.5 hover:bg-gray-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 max-w-4xl">
          {(
            [
              { id: 'need_action' as LaneId, value: needAction, label: 'Need action' },
              { id: 'waiting' as LaneId, value: waiting, label: 'Waiting' },
              { id: 'muted' as LaneId, value: muted, label: 'Muted' },
              { id: 'watching' as LaneId, value: watching, label: 'Watching' },
              { id: 'done' as LaneId, value: resolvedToday, label: 'Resolved today' },
            ] as const
          ).map((stat) => (
            <button
              key={stat.id}
              type="button"
              onClick={() => setLane(stat.id)}
              className={`text-left bg-white border rounded-lg px-4 py-3 ${
                lane === stat.id ? 'border-blue-500 ring-1 ring-blue-200' : 'border-gray-200'
              }`}
            >
              <div className="text-2xl font-semibold text-gray-900">{stat.value}</div>
              <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
            </button>
          ))}
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`text-sm rounded-full px-3 py-1 border ${
                  filter === f.id
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {lane === 'need_action' && (
            <div className="flex flex-wrap gap-2">
              {OWNERSHIP_FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setOwnership(f.id)}
                  className={`text-sm rounded-full px-3 py-1 border ${
                    ownership === f.id
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {f.label}
                  <span className={`ml-1.5 text-xs ${ownership === f.id ? 'text-blue-100' : 'text-gray-500'}`}>
                    {ownershipCounts[f.id]}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {loading && !data ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-12 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading your queue…
          </div>
        ) : !data?.managesTeam && needAction === 0 && waiting === 0 && watching === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg px-6 py-12 text-center">
            <p className="text-sm font-medium text-gray-900">You do not manage anyone yet</p>
            <p className="text-sm text-gray-500 mt-1">
              You will see people in your reporting line (including skip-level) and location staff with no line manager.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
            <div className="lg:col-span-5 bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 text-sm font-semibold text-gray-900">
                Queue
              </div>
              {items.length === 0 ? (
                <p className="px-4 py-10 text-sm text-gray-500 text-center">
                  Nothing in this status
                  {filter !== 'all' ? ' and type' : ''}
                  {lane === 'need_action' && ownership !== 'all' ? ' and ownership' : ''}
                  .
                </p>
              ) : (
                <ul className="divide-y divide-gray-100 max-h-[70vh] overflow-y-auto">
                  {items.map((item) => {
                    const active = selected?.id === item.id;
                    const age = inboxAgeLabel(item);
                    const tone = inboxAgeTone(item);
                    const since = inboxSince(item);
                    return (
                      <li key={item.id}>
                        <div className={`flex items-stretch ${inboxRowAccent(tone)} ${active ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedId(item.id);
                              setNoteAction(null);
                              setMuteOpen(false);
                              setActionError(null);
                            }}
                            className="flex-1 min-w-0 text-left px-4 py-3"
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              {age && (
                                <span
                                  title={since ? `In inbox since ${since.toLocaleString()}` : undefined}
                                  className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border ${inboxAgeClass(tone)}`}
                                >
                                  <Clock className="h-3 w-3" />
                                  {age}
                                </span>
                              )}
                              <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded border ${statusClass(item.status, item.outcome)}`}>
                                {item.statusLabel || 'Open'}
                              </span>
                              <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded border ${severityClass(item.severity)}`}>
                                {severityLabel(item.severity)}
                              </span>
                              <span className="text-sm font-medium text-gray-900 truncate">{item.title}</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1 truncate">
                              {item.positionTitle ? `${item.positionTitle} · ` : ''}
                              {item.locationName || '—'}
                              {item.managerSource === 'lineManager'
                                ? ' · in your reporting line'
                                : item.managerSource === 'locationWatcher'
                                  ? ' · watching as location manager'
                                  : ' · location default'}
                              {item.ownerName ? ` · owner ${item.ownerName}` : ''}
                            </p>
                          </button>
                          <button
                            type="button"
                            title={(item.unreadCount ?? 0) > 0
                              ? `${item.unreadCount} new messages`
                              : 'Open conversation'}
                            aria-label={(item.unreadCount ?? 0) > 0
                              ? `Open conversation, ${item.unreadCount} new`
                              : 'Open conversation'}
                            onClick={() => void openChat(item)}
                            className="relative flex-shrink-0 px-3 text-gray-500 hover:text-blue-700 disabled:opacity-50"
                          >
                            <MessageSquare className="h-4 w-4" />
                            <InboxUnreadPip count={item.unreadCount} />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="lg:col-span-7 bg-white border border-gray-200 rounded-lg min-h-[280px]">
              {!selected ? (
                <p className="px-6 py-12 text-sm text-gray-500 text-center">Select an item to act on it.</p>
              ) : (
                <div className="px-6 py-5 space-y-4">
                  <div>
                    <div className="flex flex-wrap gap-1.5">
                      {inboxAgeLabel(selected) && (
                        <span
                          title={inboxSince(selected) ? `In inbox since ${inboxSince(selected)!.toLocaleString()}` : undefined}
                          className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border ${inboxAgeClass(inboxAgeTone(selected))}`}
                        >
                          <Clock className="h-3 w-3" />
                          {inboxAgeLabel(selected)}
                        </span>
                      )}
                      <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded border ${statusClass(selected.status, selected.outcome)}`}>
                        {selected.statusLabel}
                      </span>
                      {selected.viewerRole === 'escalated' && (
                        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded border bg-orange-50 text-orange-800 border-orange-200">
                          Escalated to you
                        </span>
                      )}
                      {selected.viewerRole === 'watcher' && (
                        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded border bg-gray-50 text-gray-600 border-gray-200">
                          Watching
                        </span>
                      )}
                      {selected.viewerRole === 'owner' && (
                        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded border bg-blue-50 text-blue-800 border-blue-200">
                          You own this
                        </span>
                      )}
                    </div>
                    <h2 className="text-lg font-semibold text-gray-900 mt-2">{selected.title}</h2>
                    {selected.statusMeaning && (
                      <p className="text-xs text-gray-500 mt-1">{selected.statusMeaning}</p>
                    )}
                    <p className="text-sm text-gray-600 mt-2">{selected.why}</p>
                    {selected.recommended && (
                      <p className="text-sm text-gray-800 mt-3">
                        <span className="font-medium">Recommended: </span>
                        {selected.recommended}
                      </p>
                    )}
                    {selected.mutedUntil && (
                      <p className="text-sm text-amber-900 mt-3 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                        Muted until {formatCompanyDateTime(selected.mutedUntil)}. The exception is still open.
                      </p>
                    )}
                    <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2">History</p>
                      <ol className="space-y-2.5">
                        {(selected.history && selected.history.length > 0
                          ? selected.history
                          : selected.createdAt
                            ? [{ at: selected.createdAt, action: 'generated', label: 'Generated' }]
                            : []
                        ).map((entry, index) => (
                          <li key={`${entry.at}-${entry.action}-${index}`} className="text-sm">
                            <p className="font-medium text-gray-900">{entry.label}</p>
                            <p className="text-xs text-gray-500">
                              {formatCompanyDateTime(entry.at)}
                              {entry.actorName ? ` · ${entry.actorName}` : ''}
                            </p>
                            {'note' in entry && entry.note ? (
                              <p className="text-xs text-gray-600 mt-0.5">{entry.note}</p>
                            ) : null}
                          </li>
                        ))}
                      </ol>
                      {!(selected.history && selected.history.length > 0) && !selected.createdAt && (
                        <p className="text-xs text-gray-500">No history for this item yet.</p>
                      )}
                    </div>
                    {(() => {
                      const paths =
                        selected.statusPaths && selected.statusPaths.length > 0
                          ? selected.statusPaths
                          : selected.statusPath && selected.statusPath.length > 0
                            ? [{ name: 'Path', steps: selected.statusPath }]
                            : [];
                      if (paths.length === 0) return null;
                      return (
                        <div className="mt-3 space-y-1.5">
                          <p className="text-xs font-medium text-gray-500">Available paths</p>
                          {paths.map((path) => {
                            const onThisPath = path.steps.some((step) => step.key === selected.status);
                            return (
                              <p
                                key={path.name}
                                className={`text-xs ${onThisPath ? 'text-gray-700' : 'text-gray-400'}`}
                              >
                                <span className="font-medium">{path.name}: </span>
                                {path.steps.map((step, index) => (
                                  <span key={`${path.name}-${step.key}-${index}`}>
                                    {index > 0 ? ' → ' : ''}
                                    <span
                                      className={
                                        step.key === selected.status ? 'text-gray-900 font-semibold' : ''
                                      }
                                    >
                                      {step.label}
                                    </span>
                                  </span>
                                ))}
                              </p>
                            );
                          })}
                        </div>
                      );
                    })()}
                    {selected.managerNote && (
                      <p className="text-sm text-gray-700 mt-3 bg-gray-50 border border-gray-100 rounded-md px-3 py-2">
                        <span className="font-medium">Asked: </span>
                        {selected.managerNote}
                      </p>
                    )}
                    {selected.employeeReply && (
                      <p className="text-sm text-gray-700 mt-2 bg-teal-50 border border-teal-100 rounded-md px-3 py-2">
                        <span className="font-medium">Employee: </span>
                        {selected.employeeReply}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-3">
                      Owner: {selected.ownerName || 'Unassigned'}
                      {selected.escalatedToName ? ` · Escalated to ${selected.escalatedToName}` : ''}
                      {inboxAgeLabel(selected) ? ` · ${inboxAgeLabel(selected)}` : ''}
                      {typeof selected.workingDaysPending === 'number' && selected.lane !== 'done'
                        ? ` · ${selected.workingDaysPending}/${selected.escalationWorkingDays ?? 0} working days toward escalation`
                        : ''}
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      {selected.managerSource === 'lineManager'
                        ? 'They sit in your reporting line (direct or skip-level).'
                        : selected.managerSource === 'locationWatcher'
                          ? 'You are the location manager watching this. The line manager still owns it until you take over.'
                          : 'No line manager is set — you are the location manager and own this.'}
                    </p>
                  </div>

                  {actionError && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                      {actionError}
                    </div>
                  )}

                  {muteOpen && selected.actions.includes('mute') && (
                    <div className="border border-gray-200 rounded-md p-3 space-y-2">
                      <p className="text-sm text-gray-800 font-medium">Mute until</p>
                      <p className="text-xs text-gray-500">
                        The exception stays open. This only hides it from Need action until the time you pick (max 14 days). Escalation still runs.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={busy}
                          className="text-sm px-3 py-1.5 border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50"
                          onClick={() => void muteUntil(selected, endOfLocalDay(new Date()))}
                        >
                          End of today
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          className="text-sm px-3 py-1.5 border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50"
                          onClick={() => void muteUntil(selected, nextWorkingDayEnd())}
                        >
                          Next working day
                        </button>
                      </div>
                      <label className="text-sm text-gray-700 block">
                        Pick a date
                        <input
                          type="date"
                          className="mt-1 w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm"
                          min={toDateInputValue(new Date())}
                          max={toDateInputValue(muteMaxDate())}
                          value={muteDate}
                          onChange={(e) => setMuteDate(e.target.value)}
                        />
                      </label>
                      <div className="flex gap-2 justify-end">
                        <button
                          type="button"
                          className="text-sm px-3 py-1.5 border border-gray-200 rounded-md"
                          onClick={() => setMuteOpen(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={busy || !muteDate}
                          className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-md disabled:opacity-50"
                          onClick={() => {
                            const picked = new Date(`${muteDate}T00:00:00`);
                            if (Number.isNaN(picked.getTime())) return;
                            void muteUntil(selected, endOfLocalDay(picked));
                          }}
                        >
                          Mute
                        </button>
                      </div>
                    </div>
                  )}

                  {noteAction && (
                    <div className="border border-gray-200 rounded-md p-3 space-y-2">
                      <label className="text-sm text-gray-700 block">
                        {notePrompt(noteAction)}
                        <textarea
                          className="mt-1 w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm"
                          rows={2}
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                        />
                      </label>
                      <div className="flex gap-2 justify-end">
                        <button
                          type="button"
                          className="text-sm px-3 py-1.5 border border-gray-200 rounded-md"
                          onClick={() => setNoteAction(null)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={busy || (noteAction !== 'mark_responded' && !note.trim())}
                          className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-md disabled:opacity-50"
                          onClick={() => submitNote(selected)}
                        >
                          Confirm
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 pt-1">
                    {selected.actions.includes('take_over') && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void runTransition(selected, 'take_over')}
                        className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
                      >
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                        Take over
                      </button>
                    )}
                    {selected.actions.includes('approve') && selected.type !== 'timecard' && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void runTransition(selected, 'approve')}
                        className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                        Approve
                      </button>
                    )}
                    {selected.actions.includes('deny') && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          setNoteAction('deny');
                          setActionError(null);
                        }}
                        className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        <X className="h-3.5 w-3.5" />
                        Deny
                      </button>
                    )}
                    {selected.actions.includes('ask_manager') && (
                      <InboxChatButton
                        count={selected.managerUnreadCount}
                        label="Ask Manager"
                        onClick={() => void openChat(selected, { intent: 'ask_manager' })}
                      />
                    )}
                    {selected.actions.includes('request_info') && (
                      <InboxChatButton
                        count={selected.employeeUnreadCount}
                        label="Ask Employee"
                        onClick={() => void openChat(selected, { intent: 'request_info' })}
                      />
                    )}
                    <InboxChatButton
                      count={selected.unreadCount}
                      label="Chat"
                      onClick={() => void openChat(selected)}
                    />
                    {(selected.viewerRole !== 'watcher'
                      && (selected.actions.includes('request_info')
                        || selected.actions.includes('ask_manager')
                        || selected.actions.includes('open_chat'))) && (
                      <button
                        type="button"
                        disabled={!selected.hasPhone}
                        title={selected.hasPhone ? 'Send SMS' : 'No phone number on employee profile'}
                        onClick={() => void openChat(selected, { openSms: true, intent: 'request_info' })}
                        className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Phone className="h-3.5 w-3.5" />
                        Send SMS
                      </button>
                    )}
                    {selected.actions.includes('mark_responded') && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          setNoteAction('mark_responded');
                          setActionError(null);
                        }}
                        className="text-sm px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Employee responded
                      </button>
                    )}
                    {selected.actions.includes('mute') && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => {
                          setMuteOpen(true);
                          setMuteDate(toDateInputValue(nextWorkingDayEnd()));
                          setActionError(null);
                        }}
                        className="text-sm px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Mute until
                      </button>
                    )}
                    {selected.actions.includes('unmute') && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void runTransition(selected, 'unmute')}
                        className="text-sm px-3 py-1.5 rounded-md border border-amber-200 text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                      >
                        Unmute
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setDetailsItem(selected)}
                      className={
                        (selected.type === 'timecard' && selected.actions.includes('approve'))
                        || needsEditToResolve(selected.type)
                          ? 'inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700'
                          : 'text-sm px-3 py-1.5 rounded-md border border-violet-200 text-violet-800 hover:bg-violet-50'
                      }
                    >
                      {selected.type === 'timecard' && selected.actions.includes('approve') ? (
                        <>
                          <Check className="h-3.5 w-3.5" />
                          Review & approve
                        </>
                      ) : needsEditToResolve(selected.type) ? (
                        <>
                          <Check className="h-3.5 w-3.5" />
                          Edit visa or licence
                        </>
                      ) : (
                        inboxDetailsLabel(selected.type)
                      )}
                    </button>
                    {selected.deepLink && selected.actions.includes('open') && (
                      <button
                        type="button"
                        onClick={() => openPathInNewWindow(selected.deepLink!)}
                        className="text-sm px-3 py-1.5 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50"
                      >
                        Open full page
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {settingsOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 px-4">
          <div className="bg-white rounded-lg border border-gray-200 w-full max-w-lg p-5 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">Escalation after X working days</h2>
            <p className="text-sm text-gray-500">
              Each type has its own clock. Escalation notifies the next manager in the line; it does not change the owner.
            </p>
            <div className="max-h-80 overflow-y-auto space-y-2">
              {settings.map((row) => (
                <label key={row.type} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-800">{row.label}</span>
                  <input
                    type="number"
                    min={0}
                    max={30}
                    className="w-20 border border-gray-200 rounded-md px-2 py-1 text-sm"
                    value={row.escalationWorkingDays}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setSettings((current) =>
                        current.map((s) =>
                          s.type === row.type
                            ? { ...s, escalationWorkingDays: Number.isFinite(value) ? value : 0 }
                            : s,
                        ),
                      );
                    }}
                  />
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="text-sm px-3 py-1.5 border border-gray-200 rounded-md"
                onClick={() => setSettingsOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={settingsBusy}
                className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-md disabled:opacity-50"
                onClick={() => void saveSettings()}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {chatItem && (
        <InboxChatModal
          item={chatItem}
          messageId={chatMessageId}
          conversations={chatConversations}
          onConversationsChange={setChatConversations}
          smsOpen={smsComposer}
          sendingSms={sendingSms}
          intent={chatIntent}
          onClose={closeChat}
          onMessageSent={(text) => handleChatMessageSent(chatItem, text)}
          onSendSms={(message) => handleSendSms(chatItem, message)}
        />
      )}

      {detailsItem && (
        <InboxItemDetailsModal
          item={detailsItem}
          onClose={() => setDetailsItem(null)}
          onTimecardApproved={() => {
            setDetailsItem(null);
            void load();
          }}
          onInboxFixed={() => {
            void markInboxFixed(detailsItem);
          }}
        />
      )}
    </div>
  );
};

export default ManagerInbox;
